import Immutable from 'immutable';

import type {
    BulkUpdateProjectMembersRequest,
    DashboardVideo,
    PatchedProjectRequest,
} from 'js/src/client';
import { ActionEnum, ApiService, EstimatedStatusEnum } from 'js/src/client';
import { Toast } from 'js/src/components/common/Toast';
import { handleDownloadErrors } from 'js/src/components/share-video/share-video-hooks';
import { loadTeamMembers, selectWorkspaceByWorkspaceId } from 'js/src/data/account';
import {
    clearVideos,
    deleteVideoFromStore,
    deleteVideoFromView,
    setVideoSearchTerm,
    storeRenderEstimates,
    storeVideos,
    updateVideoProperty,
} from 'js/src/data/dashboard/dashboard-actions';
import {
    getVideoSearchTerm,
    getVideoSharePageUrl,
    selectActiveView,
    selectVideoById,
    selectViewName,
    viewIsLoaded,
} from 'js/src/data/dashboard/dashboard-selectors';
import { ALL_VIDEOS_VIEW_ID, TRASH_VIDEOS_VIEW_ID } from 'js/src/data/dashboard/dashboard-types';
import { type ImmutableMap } from 'js/src/libs/consts';
import { pluralize } from 'js/src/libs/custom-utils';
import { ErrorReporter } from 'js/src/libs/error-reporter';
import Navigation from 'js/src/libs/navigation';
import type { AppDispatch } from 'js/src/libs/RootState';
import VideoDownloader from 'js/src/libs/video-downloader';
import type { GetRootState } from 'js/src/luminary/luminary-redux-connect-types';
import { isApiError, isApiErrorWithProperty } from 'js/src/types';

let keepPollingRenderEstimates: undefined | boolean;

export function startPollingEstimatedRenderDurations() {
    return (dispatch: AppDispatch) => {
        if (keepPollingRenderEstimates === undefined) {
            // we are not already polling, so we should start.
            dispatch(loadEstimatedRenderDurations());
        }
    };
}

export function loadEstimatedRenderDurations() {
    return async (dispatch: AppDispatch) => {
        let response;
        try {
            response = await ApiService.queueEstimatedRenderDurationRetrieve();
        } catch (error) {
            ErrorReporter.capture(error);
            return;
        }

        dispatch(storeRenderEstimates(response.data.video_estimates));

        const remaining = Object.values(response.data.video_estimates).filter(
            (est) => est.estimated_status !== EstimatedStatusEnum.ERROR,
        );
        keepPollingRenderEstimates = remaining.length > 0;

        setTimeout(() => {
            if (keepPollingRenderEstimates) {
                dispatch(loadEstimatedRenderDurations());
            }
        }, 20000);
    };
}

export function updateVideoName(name: string, videoId: number) {
    return (dispatch: AppDispatch, getState: GetRootState) => {
        const state = getState();
        const video = selectVideoById(state, videoId);
        if (!video) throw new Error('Video not found');
        const originalName = video.get('name');
        dispatch(
            updateVideoProperty({
                videoId,
                property: 'name',
                newValue: name,
            }),
        );
        ApiService.videoPartialUpdate({
            path: { video_id: videoId.toString() },
            body: { name },
        })
            .then((response) => {
                dispatch(
                    updateVideoProperty({
                        videoId,
                        property: 'name',
                        newValue: response.data.name,
                    }),
                );
            })
            .catch((err: unknown) => {
                dispatch(
                    updateVideoProperty({
                        videoId,
                        property: 'name',
                        newValue: originalName,
                    }),
                );
                Toast.error(
                    'Something went wrong with renaming your video, please try again later.',
                );
                ErrorReporter.capture(err);
            });
    };
}

/**
 * Ajax operation to soft archive multiple videos. Built-in functionality includes Toast message on success/error, and archiving
 * videos on success.
 *
 * @param {Array<Number>} videoIds - list of videos to be deleted. Wrap [videoId] if you want to delete just 1 video
 * @returns {Promise} - chainable Promise to perform actions on success/error
 */
export function softArchiveVideos(videoIds: Array<number>) {
    return (dispatch: AppDispatch, getState: GetRootState) => {
        return new Promise<void>((resolve, reject) => {
            const numVideos = videoIds.length;
            const state = getState();
            let videoList = Immutable.List<ImmutableMap<DashboardVideo>>();
            videoIds.forEach((videoId) => {
                const vid = selectVideoById(state, videoId);
                if (!vid) throw new Error('Video not found');
                videoList = videoList.push(vid);
            });
            dispatch(_softArchiveVideosInState(videoList));
            ApiService.videosArchiveMultipleVideosUpdate({
                body: {
                    video_ids: videoIds,
                    action: ActionEnum.SOFT_ARCHIVE,
                },
            })
                .then(() => {
                    if (numVideos > 1) {
                        Toast.success(`${numVideos} videos were deleted successfully.`);
                    } else {
                        Toast.success('Video was deleted successfully.');
                    }
                    resolve();
                })
                .catch((err) => {
                    dispatch(_restoreVideosInState(videoList));
                    Toast.error(`There was an error deleting ${pluralize(numVideos, 'video')}.`);
                    reject(err);
                });
        });
    };
}

/**
 * Ajax operation to "permanently" archive a video.
 * @param videoIds - list of videos to be deleted. Wrap with Immutable.List if you want to delete just one video
 * @returns {Promise}
 */
export function hardArchiveVideos(videoIds: Immutable.List<number>) {
    return (dispatch: AppDispatch, getState: GetRootState) => {
        return new Promise<void>((resolve, reject) => {
            const state = getState();
            const videos = videoIds.map((videoId) => {
                const vid = selectVideoById(state, videoId);
                if (!vid) throw new Error('Video not found');
                return vid;
            });
            videos.forEach((video) => {
                dispatch(
                    deleteVideoFromStore({
                        videoId: video.get('id'),
                    }),
                );
            });
            ApiService.videosArchiveMultipleVideosUpdate({
                body: {
                    video_ids: videoIds.toJS(),
                    action: ActionEnum.HARD_ARCHIVE,
                },
            })
                .then(() => {
                    resolve();
                })
                .catch((err) => {
                    dispatch(
                        storeVideos({
                            view: TRASH_VIDEOS_VIEW_ID,
                            videos,
                            addToEnd: false,
                        }),
                    );
                    reject(err);
                });
        });
    };
}

/**
 * Restore a list of videos from the trash area into the projects (or "All videos") where they came from.
 * @param videoIds -list of videoIds to be restored. Wrap with Immutable.List if you want to restore just 1 video
 * @returns {Promise}
 */
export function restoreVideos(videoIds: Immutable.List<number>) {
    return (dispatch: AppDispatch, getState: GetRootState) => {
        return new Promise<void>((resolve, reject) => {
            const state = getState();
            const videos = videoIds.map((videoId) => {
                const vid = selectVideoById(state, videoId);
                if (!vid) throw new Error('Video not found');
                return vid;
            });
            dispatch(_restoreVideosInState(videos));
            ApiService.videosArchiveMultipleVideosUpdate({
                body: {
                    video_ids: videoIds.toJS(),
                    action: ActionEnum.UNARCHIVE,
                },
            })
                .then(() => {
                    resolve();
                })
                .catch((err) => {
                    dispatch(_softArchiveVideosInState(videos));
                    reject(err);
                });
        });
    };
}

function _restoreVideosInState(videos: Immutable.List<ImmutableMap<DashboardVideo>>) {
    return (dispatch: AppDispatch) => {
        videos.forEach((video: ImmutableMap<DashboardVideo>) => {
            const project = video.get('project');
            if (project) {
                dispatch(
                    storeVideos({
                        addToEnd: false,
                        videos: Immutable.List([video]),
                        view: project,
                    }),
                );
            }
            dispatch(
                storeVideos({
                    addToEnd: false,
                    videos: Immutable.List([video]),
                    view: ALL_VIDEOS_VIEW_ID,
                }),
            );
            dispatch(
                deleteVideoFromView({
                    videoId: video.get('id'),
                    view: TRASH_VIDEOS_VIEW_ID,
                }),
            );
        });
    };
}

function _softArchiveVideosInState(videos: Immutable.List<ImmutableMap<DashboardVideo>>) {
    return (dispatch: AppDispatch) => {
        videos.forEach((video) => {
            // delete the video from everywhere
            dispatch(
                deleteVideoFromStore({
                    videoId: video.get('id'),
                }),
            );
        });
        // then add it to the trash section
        dispatch(
            storeVideos({
                addToEnd: false,
                view: TRASH_VIDEOS_VIEW_ID,
                videos,
            }),
        );
    };
}

export function resetSearchedVideos() {
    return (dispatch: AppDispatch, getState: GetRootState) => {
        // if the video search term isn't blank, clear it and reset the results
        const searchTerm = getVideoSearchTerm(getState());
        if (!searchTerm) {
            return;
        }
        dispatch(setVideoSearchTerm(''));
        dispatch(
            clearVideos({
                view: selectActiveView(getState()),
            }),
        );
    };
}

export function moveVideos(
    videoIds: number[],
    targetWorkspaceId: number,
    targetProjectId: number | null,
) {
    const numVideos = videoIds.length;
    targetProjectId = targetProjectId || null;
    return async (dispatch: AppDispatch, getState: GetRootState) => {
        const state = getState();
        const targetWorkspace = selectWorkspaceByWorkspaceId(state, targetWorkspaceId);
        const targetWorkspaceName = targetWorkspace.get('name');
        const targetProjectName =
            targetProjectId && viewIsLoaded(state, targetProjectId)
                ? selectViewName(state, targetProjectId)
                : '';
        const isDestinationInCurrentWorkspace =
            SERVER.USER.workspace_id === targetWorkspace.get('id');

        await ApiService.videosMoveVideosPartialUpdate({
            body: {
                video_ids: videoIds,
                target_project_id: targetProjectId,
                target_workspace_id: targetWorkspaceId,
            },
        });

        videoIds.forEach((videoId) => {
            const video = selectVideoById(state, videoId);
            if (!video) throw new Error('Video not found');
            const sourceProject = video.get('project');
            const updatedVideo = video.set('project', targetProjectId);
            if (isDestinationInCurrentWorkspace) {
                // update the video.project property on all the videos
                dispatch(
                    updateVideoProperty({
                        videoId,
                        property: 'project',
                        newValue: targetProjectId,
                    }),
                );

                // if we are moving to a project, add the video to that view
                if (targetProjectId) {
                    dispatch(
                        storeVideos({
                            view: targetProjectId,
                            videos: Immutable.List([updatedVideo]),
                            addToEnd: false,
                        }),
                    );
                }

                // if we are taking the video out of a project, remove the video from that view
                if (sourceProject) {
                    dispatch(
                        deleteVideoFromView({
                            view: sourceProject,
                            videoId,
                        }),
                    );
                }
            } else {
                dispatch(
                    deleteVideoFromStore({
                        videoId,
                    }),
                );
            }
        });

        let successMsg = `${numVideos} video${numVideos > 1 ? 's' : ''} ${numVideos > 1 ? 'were' : 'was'} moved to `;
        if (targetProjectName) {
            if (isDestinationInCurrentWorkspace) {
                successMsg += `${targetProjectName} successfully`;
                analytics.track('Videos moved', {
                    target_project: targetProjectName,
                });
            } else {
                successMsg += `${targetProjectName} in ${targetWorkspaceName} successfully`;
                analytics.track('Videos moved', {
                    target_project: targetProjectName,
                    target_workspace: targetWorkspaceName,
                });
            }
        } else if (!isDestinationInCurrentWorkspace && targetWorkspaceId) {
            successMsg += `${targetWorkspaceName} successfully`;
            analytics.track('Videos moved', {
                target_workspace: targetWorkspaceName,
            });
        } else {
            successMsg = 'Video removed from project';
            analytics.track('Videos removed');
        }

        Toast.success(successMsg);
    };
}

export const RENDERING_STARTED = 'rendering started';
export function requestDownloadVideo(videoId: number) {
    return (dispatch: AppDispatch, getState: GetRootState) => {
        /**
         * `renderConfig is derived based on current video format.
         *  This is further used by backend to render video as a single file or a segments.
         */
        const sharePageUrl = getVideoSharePageUrl(getState(), videoId);
        return new Promise<string>((resolve, reject) => {
            const videoDownloader = new VideoDownloader({
                videoId,
                finishedCallback: (url: string) => {
                    resolve(url);
                },
                failCallback: (error: Error) => {
                    handleDownloadErrors(error, dispatch);
                    reject(new Error());
                },
                renderingStartedCallback: () => {
                    Navigation.goToLocation(sharePageUrl);
                    reject(new Error(RENDERING_STARTED));
                },
            });
            videoDownloader.download();
        });
    };
}

export function updateProjectMembers(
    project_id: number,
    user_ids: Pick<PatchedProjectRequest, 'added_members' | 'removed_members'>,
) {
    return (dispatch: AppDispatch, getState: GetRootState) => {
        ApiService.projectsPartialUpdate({
            path: { id: project_id },
            body: {
                added_members: user_ids.added_members,
                removed_members: user_ids.removed_members,
            },
        })
            .then((response) => {
                // reload team members to update the project member state
                dispatch(loadTeamMembers());
            })
            .catch((error: unknown) => {
                if (
                    isApiError(error) &&
                    error.status === 400 &&
                    isApiErrorWithProperty(error, 'added_members')
                ) {
                    type ErrorBody = { added_members: string[] };
                    const result = (error.body as ErrorBody)?.added_members;
                    Toast.error(result[0]);
                } else {
                    Toast.error(
                        'There is an issue adding members to the project. Please try again later.',
                    );
                }
            });
    };
}

export function bulkUpdateProjectMembers(projects: BulkUpdateProjectMembersRequest['projects']) {
    return (dispatch: AppDispatch) => {
        ApiService.projectsBulkUpdateMembersCreate({
            body: { projects },
        })
            .then(() => {
                dispatch(loadTeamMembers());
            })
            .catch((error: unknown) => {
                if (isApiError(error) && error.status === 400) {
                    type ErrorBody = { projects?: string[] };
                    const body = error.body as ErrorBody;
                    const message = body?.projects?.[0];
                    Toast.error(
                        message ||
                            'There is an issue updating project members. Please try again later.',
                    );
                } else {
                    Toast.error(
                        'There is an issue updating project members. Please try again later.',
                    );
                }
            });
    };
}
